/**
 * EventMuhurtaPage — Event-specific Muhurta window finder.
 *
 * Users pick an event type (Wedding, Business Launch, etc.), a date range,
 * and optional filters. The page searches enriched muhurta windows via the
 * POST /v1/muhurta/windows API and shows ranked results with bookmarking
 * and calendar export.
 */
import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  muhurtaApi,
  type MuhurtaWindow,
  type MuhurtaBookmark,
} from '@/lib/api'
import { useUser } from '@/store'
import { cn } from '@/lib/utils'
import {
  Search, Filter, ChevronDown, ChevronUp,
  CheckCircle2, XCircle, AlertCircle,
  BookmarkPlus, BookmarkCheck, Download, Trash2,
  Star, BarChart2, MapPin, Calendar, RefreshCw,
  ExternalLink,
} from 'lucide-react'
import dayjs from 'dayjs'
import { useFeatureGate } from '@/hooks/useSubscription'
import { HardGate } from '@/components/paywall'

// ── Event type catalogue ──────────────────────────────────────────────────────

interface EventTypeItem {
  id:        string
  label:     string
  sanskrit:  string
  emoji:     string
  available: boolean
}

interface EventTypeGroup {
  category: string
  items:    EventTypeItem[]
}

const EVENT_TYPE_GROUPS: EventTypeGroup[] = [
  {
    category: 'Life',
    items: [
      { id: 'marriage',         label: 'Wedding',       sanskrit: 'विवाह',       emoji: '💍', available: true  },
      { id: 'house_warming',    label: 'Griha Pravesh', sanskrit: 'गृह प्रवेश', emoji: '🏡', available: false },
      { id: 'naming_ceremony',  label: 'Namkaran',      sanskrit: 'नामकरण',     emoji: '🎊', available: false },
      { id: 'thread_ceremony',  label: 'Mundan',        sanskrit: 'मुंडन',       emoji: '🌸', available: false },
    ],
  },
  {
    category: 'Business',
    items: [
      { id: 'business_launch',  label: 'Business Launch', sanskrit: 'व्यापार', emoji: '🚀', available: true  },
      { id: 'career',           label: 'New Job',         sanskrit: 'कर्म',    emoji: '💼', available: true  },
    ],
  },
  {
    category: 'Travel',
    items: [
      { id: 'travel',           label: 'Journey',    sanskrit: 'यात्रा', emoji: '✈️', available: true },
    ],
  },
  {
    category: 'Health',
    items: [
      { id: 'medical_procedure', label: 'Medical', sanskrit: 'आरोग्य', emoji: '🩺', available: true },
    ],
  },
  {
    category: 'Education',
    items: [
      { id: 'education_start', label: 'Education', sanskrit: 'विद्या', emoji: '📚', available: true },
    ],
  },
  {
    category: 'Vehicle',
    items: [
      { id: 'vehicle_purchase', label: 'Vehicle', sanskrit: 'वाहन', emoji: '🚘', available: false },
    ],
  },
]

const ALL_EVENT_ITEMS = EVENT_TYPE_GROUPS.flatMap(g => g.items)

// ── Quality config (mirrors MuhurtaPage) ─────────────────────────────────────

const QUALITY_CONFIG = {
  excellent: {
    label: 'Excellent',
    bar:   'bg-auspicious',
    text:  'text-auspicious',
    badge: 'bg-auspicious/10 text-auspicious border border-auspicious/20',
    row:   'border-l-auspicious/60',
    Icon:  CheckCircle2,
  },
  good: {
    label: 'Good',
    bar:   'bg-gold',
    text:  'text-gold',
    badge: 'bg-gold/10 text-gold border border-gold/20',
    row:   'border-l-gold/60',
    Icon:  CheckCircle2,
  },
  fair: {
    label: 'Fair',
    bar:   'bg-star-soft',
    text:  'text-star-soft',
    badge: 'bg-cosmic-dusk text-star-soft border border-cosmic-mist',
    row:   'border-l-cosmic-mist',
    Icon:  AlertCircle,
  },
  poor: {
    label: 'Poor',
    bar:   'bg-inauspicious',
    text:  'text-inauspicious',
    badge: 'bg-inauspicious/10 text-inauspicious border border-inauspicious/20',
    row:   'border-l-inauspicious/60',
    Icon:  XCircle,
  },
} as const

const SCORE_FACTORS = [
  { key: 'panchaanga'      as const, label: 'Panchanga', sanskrit: 'पञ्चाङ्ग' },
  { key: 'nakshatraFit'    as const, label: 'Nakshatra', sanskrit: 'नक्षत्र' },
  { key: 'transitStrength' as const, label: 'Transits',  sanskrit: 'गोचर' },
  { key: 'dashaHarmony'    as const, label: 'Dasha',     sanskrit: 'दशा' },
  { key: 'yogas'           as const, label: 'Yogas',     sanskrit: 'योग' },
  { key: 'lagnaStrength'   as const, label: 'Lagna',     sanskrit: 'लग्न' },
]

const NAKSHATRA_NAMES = [
  'Ashwini','Bharani','Krittika','Rohini','Mrigashira','Ardra','Punarvasu',
  'Pushya','Ashlesha','Magha','Purva Phalguni','Uttara Phalguni','Hasta',
  'Chitra','Swati','Vishakha','Anuradha','Jyeshtha','Mula','Purva Ashadha',
  'Uttara Ashadha','Shravana','Dhanishtha','Shatabhisha','Purva Bhadrapada',
  'Uttara Bhadrapada','Revati',
]

const TITHI_NAMES = [
  'Pratipada','Dwitiya','Tritiya','Chaturthi','Panchami','Shashthi','Saptami',
  'Ashtami','Navami','Dashami','Ekadashi','Dwadashi','Trayodashi','Chaturdashi',
  'Purnima/Amavasya',
]

const VARA_NAMES = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat']

const DAYS_OF_WEEK = [
  { n: 0, label: 'Sun', short: 'S' },
  { n: 1, label: 'Mon', short: 'M' },
  { n: 2, label: 'Tue', short: 'T' },
  { n: 3, label: 'Wed', short: 'W' },
  { n: 4, label: 'Thu', short: 'T' },
  { n: 5, label: 'Fri', short: 'F' },
  { n: 6, label: 'Sat', short: 'S' },
]

const DATE_PRESETS = [
  { label: '30 days', value: 30 },
  { label: '60 days', value: 60 },
  { label: '90 days', value: 90 },
]

// Default location: Mumbai
const DEFAULT_LAT = 19.076
const DEFAULT_LON = 72.8777
const DEFAULT_TZ  = 'Asia/Kolkata'

// ── Helpers ───────────────────────────────────────────────────────────────────

function windowKey(w: MuhurtaWindow) {
  return `${w.startUtc}_${w.endUtc}`
}

/** Build Google Calendar deep link for a muhurta window. */
function buildGCalLink(win: MuhurtaWindow, eventLabel: string): string {
  const start = dayjs(win.startUtc).format('YYYYMMDDTHHmmss') + 'Z'
  const end   = dayjs(win.endUtc).format('YYYYMMDDTHHmmss') + 'Z'
  const text  = encodeURIComponent(`${eventLabel} Muhurta`)
  const detail = encodeURIComponent(`Shubh Time score: ${win.score}/100 — ${win.explanation.en}`)
  return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${text}&dates=${start}/${end}&details=${detail}`
}

// ── Sub-components ────────────────────────────────────────────────────────────

// Wizard progress strip
function WizardProgress({
  selectedEventType,
  canSearch,
  hasResults,
}: {
  selectedEventType: string | null
  canSearch: boolean
  hasResults: boolean
}) {
  const steps = [
    { n: 1, label: 'Choose Event',    done: selectedEventType !== null },
    { n: 2, label: 'Date Range',      done: canSearch },
    { n: 3, label: 'View Results',    done: hasResults },
  ]

  return (
    <div className="flex items-center gap-0">
      {steps.map((step, i) => (
        <div key={step.n} className="flex items-center flex-1 min-w-0">
          {/* Step pill */}
          <div className={cn(
            'flex items-center gap-1.5 flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium border transition-all duration-300',
            step.done
              ? 'bg-violet/15 border-violet/40 text-violet-glow'
              : i === 0 || steps[i - 1].done
                ? 'bg-cosmic-dusk border-cosmic-mist text-star-bright'
                : 'bg-transparent border-cosmic-mist/50 text-star-dim',
          )}>
            {step.done
              ? <CheckCircle2 size={12} className="text-auspicious flex-shrink-0" />
              : <span className="w-4 h-4 rounded-full text-2xs flex items-center justify-center font-bold bg-cosmic-mist/30">{step.n}</span>
            }
            <span className="hidden sm:block truncate">{step.label}</span>
          </div>
          {/* Connector */}
          {i < steps.length - 1 && (
            <div className="flex-1 h-px mx-1.5 transition-all duration-500"
              style={{
                background: steps[i].done
                  ? 'linear-gradient(90deg, rgba(107,33,168,0.6), rgba(107,33,168,0.2))'
                  : 'rgba(26,26,82,1)',
              }}
            />
          )}
        </div>
      ))}
    </div>
  )
}

// Event type selector grid
function EventTypeGrid({
  selected,
  onSelect,
}: {
  selected: string | null
  onSelect: (id: string) => void
}) {
  return (
    <div className="space-y-4">
      {EVENT_TYPE_GROUPS.map(group => (
        <div key={group.category}>
          <p className="text-star-dim text-2xs font-mono uppercase tracking-wider mb-2">
            {group.category}
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {group.items.map(item => (
              <button
                key={item.id}
                onClick={() => item.available && onSelect(item.id)}
                disabled={!item.available}
                className={cn(
                  'relative flex flex-col items-center gap-1.5 p-3 rounded-xl border transition-all text-center',
                  item.available
                    ? selected === item.id
                      ? 'bg-violet/10 border-violet text-star-white shadow-glow-violet'
                      : 'bg-cosmic-nebula border-cosmic-mist text-star-muted hover:border-violet/40 hover:text-star-bright'
                    : 'bg-cosmic-void border-cosmic-mist/50 text-star-dim opacity-50 cursor-not-allowed',
                )}
              >
                <span className="text-2xl leading-none">{item.emoji}</span>
                <span className="text-xs font-medium leading-tight">{item.label}</span>
                <span className="text-2xs font-display text-star-dim">{item.sanskrit}</span>
                {!item.available && (
                  <span className="absolute top-1 right-1 px-1 py-0.5 rounded text-[9px] font-mono bg-cosmic-dusk text-star-dim border border-cosmic-mist">
                    soon
                  </span>
                )}
                {selected === item.id && (
                  <span className="absolute top-1 right-1 w-3 h-3 rounded-full bg-violet flex items-center justify-center">
                    <CheckCircle2 className="w-2.5 h-2.5 text-white" />
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

// Date range picker with presets + custom
function DateRangePicker({
  preset,
  customStart,
  customEnd,
  onPreset,
  onCustomStart,
  onCustomEnd,
}: {
  preset:        number | null
  customStart:   string
  customEnd:     string
  onPreset:      (days: number) => void
  onCustomStart: (d: string) => void
  onCustomEnd:   (d: string) => void
}) {
  const today = dayjs().format('YYYY-MM-DD')

  return (
    <div className="space-y-3">
      {/* Preset buttons */}
      <div className="flex items-center gap-2 flex-wrap">
        {DATE_PRESETS.map(p => (
          <button
            key={p.value}
            onClick={() => onPreset(p.value)}
            className={cn(
              'px-3 py-1.5 rounded-lg text-xs font-mono border transition-all',
              preset === p.value
                ? 'bg-gold/10 text-gold border-gold/30'
                : 'bg-cosmic-dusk text-star-muted border-cosmic-mist hover:text-star-bright',
            )}
          >
            {p.label}
          </button>
        ))}
        <button
          onClick={() => onPreset(0)}
          className={cn(
            'px-3 py-1.5 rounded-lg text-xs font-mono border transition-all',
            preset === 0
              ? 'bg-gold/10 text-gold border-gold/30'
              : 'bg-cosmic-dusk text-star-muted border-cosmic-mist hover:text-star-bright',
          )}
        >
          Custom
        </button>
      </div>

      {/* Custom range inputs — shown when custom selected */}
      {preset === 0 && (
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <label className="text-star-dim text-2xs font-mono">From</label>
            <input
              type="date"
              value={customStart}
              min={today}
              onChange={e => onCustomStart(e.target.value)}
              className="bg-cosmic-dusk border border-cosmic-mist rounded px-2 py-1 text-sm text-star-bright font-mono focus:outline-none focus:border-gold/50"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-star-dim text-2xs font-mono">To</label>
            <input
              type="date"
              value={customEnd}
              min={customStart || today}
              onChange={e => onCustomEnd(e.target.value)}
              className="bg-cosmic-dusk border border-cosmic-mist rounded px-2 py-1 text-sm text-star-bright font-mono focus:outline-none focus:border-gold/50"
            />
          </div>
        </div>
      )}

      {/* Show computed range summary */}
      {(preset !== null && preset !== 0) && (
        <p className="text-star-dim text-2xs font-mono">
          {dayjs().format('D MMM YYYY')} – {dayjs().add(preset, 'day').format('D MMM YYYY')}
        </p>
      )}
    </div>
  )
}

// Result window row
interface ResultRowProps {
  win:          MuhurtaWindow
  rank:         number
  isExpanded:   boolean
  isBookmarked: boolean
  isBookmarking: boolean
  eventLabel:   string
  onToggle:     () => void
  onBookmark:   (note: string) => void
}

function ResultRow({
  win, rank, isExpanded, isBookmarked, isBookmarking,
  eventLabel, onToggle, onBookmark,
}: ResultRowProps) {
  const cfg      = QUALITY_CONFIG[win.qualityLabel]
  const date     = dayjs(win.startUtc).format('ddd, D MMM')
  const start    = dayjs(win.startUtc).format('HH:mm')
  const end      = dayjs(win.endUtc).format('HH:mm')
  const dur      = dayjs(win.endUtc).diff(dayjs(win.startUtc), 'minute')
  const nakName  = NAKSHATRA_NAMES[win.meta.nakshatra - 1] ?? `#${win.meta.nakshatra}`
  const titName  = TITHI_NAMES[((win.meta.tithi - 1) % 15)] ?? `#${win.meta.tithi}`
  const varaName = VARA_NAMES[win.meta.vara] ?? `Vara ${win.meta.vara}`
  const gcalUrl  = buildGCalLink(win, eventLabel)

  const [showNote, setShowNote] = useState(false)
  const [note, setNote]         = useState('')
  const [lang, setLang]         = useState<'en' | 'hi'>('en')

  return (
    <div
      className={cn(
        'rounded-xl border-l-2 overflow-hidden transition-colors duration-150',
        'bg-cosmic-nebula border border-cosmic-mist',
        cfg.row,
        win.recommendation && 'ring-1 ring-gold/20',
      )}
    >
      {/* Main row */}
      <div className="flex items-center gap-3 px-4 pt-3 pb-2">

        {/* Rank */}
        <span className="flex-shrink-0 w-6 text-center text-star-dim text-2xs font-mono">
          #{rank}
        </span>

        {/* Date + time */}
        <button
          onClick={onToggle}
          className="flex-shrink-0 text-left hover:opacity-80 transition-opacity min-w-[7rem]"
          aria-expanded={isExpanded}
        >
          <p className="text-star-bright text-xs font-mono leading-none">{date}</p>
          <p className="text-star-dim text-2xs mt-0.5 font-mono">{start}–{end} · {dur}m</p>
        </button>

        {/* Badges */}
        <button
          onClick={onToggle}
          className="flex items-center gap-2 flex-shrink-0 flex-wrap text-left"
          aria-expanded={isExpanded}
        >
          <span className={cn('px-2 py-0.5 rounded text-2xs font-medium', cfg.badge)}>
            {cfg.label}
          </span>
          {win.recommendation && (
            <span className="hidden sm:flex px-2 py-0.5 rounded text-2xs bg-gold/10 text-gold border border-gold/20 items-center gap-1">
              <Star className="w-2.5 h-2.5" />
              Top pick
            </span>
          )}
        </button>

        {/* Score bar */}
        <button
          onClick={onToggle}
          className="flex-1 flex items-center gap-3 min-w-0 text-left"
        >
          <div className="flex-1 h-1.5 rounded-full bg-cosmic-mist overflow-hidden">
            <div
              className={cn('h-full rounded-full transition-all duration-500', cfg.bar)}
              style={{ width: `${win.score}%` }}
            />
          </div>
          <span className={cn('text-sm font-mono font-semibold flex-shrink-0 w-8 text-right', cfg.text)}>
            {win.score}
          </span>
        </button>

        {/* Bookmark button */}
        <button
          onClick={() => { if (!isBookmarked) setShowNote(v => !v) }}
          className={cn(
            'flex-shrink-0 p-1.5 rounded-lg border transition-all',
            isBookmarked
              ? 'bg-gold/10 border-gold/20 text-gold cursor-default'
              : 'bg-cosmic-dusk border-cosmic-mist text-star-muted hover:text-gold hover:border-gold/30',
          )}
          title={isBookmarked ? 'Bookmarked' : 'Bookmark this window'}
        >
          {isBookmarked
            ? <BookmarkCheck className="w-3.5 h-3.5" />
            : <BookmarkPlus className="w-3.5 h-3.5" />
          }
        </button>

        {/* Expand toggle */}
        <button
          onClick={onToggle}
          className="flex-shrink-0 text-star-dim"
          aria-expanded={isExpanded}
        >
          {isExpanded
            ? <ChevronUp className="w-4 h-4" />
            : <ChevronDown className="w-4 h-4" />
          }
        </button>
      </div>

      {/* Inline bookmark note */}
      {showNote && !isBookmarked && (
        <div className="px-4 pb-3">
          <div className="p-3 rounded-lg bg-cosmic-void border border-gold/30 space-y-2">
            <p className="text-gold text-2xs font-mono uppercase tracking-wider">Add a note (optional)</p>
            <input
              type="text"
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder={`e.g. ${eventLabel} ceremony`}
              className="w-full bg-cosmic-dusk border border-cosmic-mist rounded px-3 py-1.5 text-sm text-star-bright placeholder:text-star-dim focus:outline-none focus:border-gold/50"
              maxLength={120}
            />
            <div className="flex items-center gap-2">
              <button
                onClick={() => { onBookmark(note); setShowNote(false); setNote('') }}
                disabled={isBookmarking}
                className="px-3 py-1 rounded text-2xs font-medium bg-gold/10 text-gold border border-gold/20 hover:bg-gold/20 transition-colors disabled:opacity-50"
              >
                {isBookmarking ? 'Saving…' : 'Bookmark'}
              </button>
              <button
                onClick={() => { setShowNote(false); setNote('') }}
                className="px-3 py-1 rounded text-2xs text-star-dim hover:text-star-soft border border-cosmic-mist transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Expanded detail */}
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-cosmic-mist/50 pt-4 space-y-4">

          {/* Explanation — bilingual */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-star-dim text-2xs font-mono uppercase tracking-wider">Analysis</p>
              <div className="flex items-center gap-1">
                {(['en', 'hi'] as const).map(l => (
                  <button
                    key={l}
                    onClick={() => setLang(l)}
                    className={cn(
                      'px-2 py-0.5 rounded text-2xs font-mono border transition-colors',
                      lang === l
                        ? 'bg-violet/10 text-violet-glow border-violet/20'
                        : 'bg-cosmic-dusk text-star-dim border-cosmic-mist hover:text-star-soft',
                    )}
                  >
                    {l === 'en' ? 'EN' : 'हि'}
                  </button>
                ))}
              </div>
            </div>
            <p className={cn('text-sm leading-relaxed', lang === 'hi' ? 'font-display text-star-soft' : 'text-star-bright')}>
              {win.explanation[lang]}
            </p>
          </div>

          {/* Score breakdown */}
          <div className="space-y-2">
            <div className="flex items-center gap-1.5">
              <BarChart2 className="w-3 h-3 text-star-dim" />
              <p className="text-star-dim text-2xs font-mono uppercase tracking-wider">Score Breakdown</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {SCORE_FACTORS.map(({ key, label, sanskrit }) => {
                const val = win.scoreBreakdown[key]
                return (
                  <div key={key} className="bg-cosmic-dusk rounded-lg p-2.5 space-y-1.5">
                    <div className="flex items-baseline justify-between gap-1">
                      <span className="text-star-bright text-xs font-medium">{label}</span>
                      <span className="text-star-dim text-2xs font-display">{sanskrit}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1 rounded-full bg-cosmic-mist overflow-hidden">
                        <div
                          className={cn(
                            'h-full rounded-full',
                            val >= 75 ? 'bg-auspicious' : val >= 50 ? 'bg-gold' : 'bg-star-muted'
                          )}
                          style={{ width: `${val}%` }}
                        />
                      </div>
                      <span className="text-2xs font-mono text-star-muted flex-shrink-0 w-7 text-right">{val}</span>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Vedic meta */}
          <div className="flex flex-wrap gap-2">
            {[
              { label: 'Nakshatra', value: nakName },
              { label: 'Tithi',     value: titName },
              { label: 'Vara',      value: varaName },
              {
                label: 'Rahu Kaal',
                value: win.meta.isRahuKalaFree ? 'Free ✓' : 'Active',
                cls:   win.meta.isRahuKalaFree ? 'text-auspicious' : 'text-inauspicious',
              },
            ].map(({ label, value, cls }) => (
              <div key={label} className="px-3 py-1.5 rounded-lg bg-cosmic-dusk border border-cosmic-mist">
                <span className="text-star-dim text-2xs font-mono">{label}: </span>
                <span className={cn('text-xs font-medium', cls ?? 'text-star-soft')}>{value}</span>
              </div>
            ))}
          </div>

          {/* Calendar links */}
          <div className="flex items-center gap-2 flex-wrap pt-1">
            <a
              href={gcalUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-cosmic-dusk border border-cosmic-mist text-star-muted hover:text-auspicious hover:border-auspicious/30 text-xs transition-colors"
            >
              <ExternalLink className="w-3 h-3" />
              Google Calendar
            </a>
          </div>
        </div>
      )}
    </div>
  )
}

// Bookmarks panel
function BookmarksPanel({
  bookmarks,
  onDelete,
  isDeleting,
}: {
  bookmarks:  MuhurtaBookmark[]
  onDelete:   (id: string) => void
  isDeleting: boolean
}) {
  if (bookmarks.length === 0) {
    return (
      <div className="flex flex-col items-center py-12 text-center">
        <span className="text-4xl mb-3">🔖</span>
        <p className="text-star-soft text-sm">No bookmarks yet.</p>
        <p className="text-star-dim text-2xs mt-1">
          Click the bookmark icon on any result window to save it.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      {bookmarks.map(bm => {
        const eventItem = ALL_EVENT_ITEMS.find(e => e.id === bm.eventType)
        const label = eventItem?.label ?? bm.eventType
        const emoji = eventItem?.emoji ?? '📅'
        const gcalUrl = buildGCalLink(
          {
            startUtc:       bm.windowStartUtc,
            endUtc:         bm.windowEndUtc,
            score:          bm.score,
            qualityLabel:   'excellent',
            recommendation: false,
            scoreBreakdown: { panchaanga: 0, nakshatraFit: 0, transitStrength: 0, dashaHarmony: 0, yogas: 0, lagnaStrength: 0 },
            explanation:    { en: '', hi: '' },
            meta:           { nakshatra: 1, tithi: 1, vara: 0, isRahuKalaFree: true },
          },
          label,
        )

        return (
          <div
            key={bm.id}
            className="flex items-center gap-3 p-3 rounded-xl bg-cosmic-nebula border border-cosmic-mist"
          >
            <span className="flex-shrink-0 text-xl">{emoji}</span>
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="text-star-bright text-sm font-mono">
                  {dayjs(bm.windowStartUtc).format('D MMM · HH:mm')} – {dayjs(bm.windowEndUtc).format('HH:mm')}
                </span>
                <span className="text-gold text-2xs font-mono">{bm.score}/100</span>
              </div>
              <p className="text-star-dim text-2xs font-medium mt-0.5 capitalize">{label}</p>
              {bm.note && (
                <p className="text-star-dim text-2xs mt-0.5 truncate italic">{bm.note}</p>
              )}
            </div>

            {/* Google Calendar */}
            <a
              href={gcalUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-shrink-0 p-1.5 rounded-lg bg-cosmic-dusk border border-cosmic-mist text-star-muted hover:text-auspicious hover:border-auspicious/30 transition-colors"
              title="Add to Google Calendar"
            >
              <ExternalLink className="w-3.5 h-3.5" />
            </a>

            {/* iCal download */}
            <a
              href={muhurtaApi.getIcalUrl(bm.id)}
              download
              className="flex-shrink-0 p-1.5 rounded-lg bg-cosmic-dusk border border-cosmic-mist text-star-muted hover:text-auspicious hover:border-auspicious/30 transition-colors"
              title="Download .ics calendar file"
            >
              <Download className="w-3.5 h-3.5" />
            </a>

            {/* Delete */}
            <button
              onClick={() => onDelete(bm.id)}
              disabled={isDeleting}
              className="flex-shrink-0 p-1.5 rounded-lg bg-cosmic-dusk border border-cosmic-mist text-star-muted hover:text-inauspicious hover:border-inauspicious/30 transition-colors disabled:opacity-40"
              title="Remove bookmark"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        )
      })}
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

type PageTab = 'find' | 'bookmarks'

export function EventMuhurtaPage() {
  const gate = useFeatureGate('event_muhurta')
  const user = useUser()
  const qc   = useQueryClient()

  // ── Form state ──────────────────────────────────────────────────────────────
  const [selectedEventType, setSelectedEventType] = useState<string | null>(null)
  const [datePreset,        setDatePreset]         = useState<number | null>(30) // 0 = custom
  const [customStart,       setCustomStart]        = useState(dayjs().format('YYYY-MM-DD'))
  const [customEnd,         setCustomEnd]          = useState(dayjs().add(30, 'day').format('YYYY-MM-DD'))
  const [minScore,          setMinScore]           = useState(0)
  const [daysOfWeek,        setDaysOfWeek]         = useState<Set<number>>(new Set())
  const [timePreference,    setTimePreference]     = useState<'all' | 'morning' | 'afternoon' | 'evening'>('all')
  const [showFilters,       setShowFilters]        = useState(false)
  const [showLocation,      setShowLocation]       = useState(false)
  const [eventLat,          setEventLat]           = useState(DEFAULT_LAT.toString())
  const [eventLon,          setEventLon]           = useState(DEFAULT_LON.toString())
  const [eventTz,           setEventTz]            = useState(user?.profile?.birthTimezone ?? DEFAULT_TZ)

  // ── UI state ────────────────────────────────────────────────────────────────
  const [pageTab,        setPageTab]        = useState<PageTab>('find')
  const [expandedKey,    setExpandedKey]    = useState<string | null>(null)
  const [bookmarkingKey, setBookmarkingKey] = useState<string | null>(null)
  const [deletingId,     setDeletingId]     = useState<string | null>(null)
  const [bookmarkedMap,  setBookmarkedMap]  = useState<Map<string, string>>(new Map()) // windowKey → bookmarkId

  // ── Queries & mutations ─────────────────────────────────────────────────────

  const searchMutation = useMutation({
    mutationFn: () => {
      const start = datePreset === 0
        ? customStart
        : dayjs().format('YYYY-MM-DD')
      const end = datePreset === 0
        ? customEnd
        : dayjs().add(datePreset ?? 30, 'day').format('YYYY-MM-DD')

      // Build birthDateTimeUtc from user profile (approx: treats local time as UTC)
      const birthDateTimeUtc = user?.profile?.dateOfBirth && user?.profile?.timeOfBirth
        ? `${user.profile.dateOfBirth}T${user.profile.timeOfBirth}.000Z`
        : '1990-01-01T00:00:00Z'

      const lat = parseFloat(eventLat) || DEFAULT_LAT
      const lon = parseFloat(eventLon) || DEFAULT_LON

      return muhurtaApi.searchWindows({
        eventType:      selectedEventType!,
        dateRangeStart: `${start}T00:00:00Z`,
        dateRangeEnd:   `${end}T23:59:59Z`,
        location: {
          latitude:  lat,
          longitude: lon,
          timezone:  eventTz,
        },
        birthData: {
          birthDateTimeUtc,
          latitude:  lat,
          longitude: lon,
          timezone:  eventTz,
        },
        limit:          90,
        minScore:       minScore > 0 ? minScore : undefined,
        daysOfWeek:     daysOfWeek.size > 0 ? Array.from(daysOfWeek) : undefined,
        timePreference: timePreference !== 'all' ? timePreference : undefined,
      })
    },
  })

  const { data: bookmarksData } = useQuery({
    queryKey: ['muhurta-bookmarks'],
    queryFn:  () => muhurtaApi.getBookmarks(),
    retry:    1,
  })

  const createBookmark = useMutation({
    mutationFn: (vars: { key: string; win: MuhurtaWindow; note: string }) =>
      muhurtaApi.createBookmark({
        windowStartUtc: vars.win.startUtc,
        windowEndUtc:   vars.win.endUtc,
        eventType:      selectedEventType ?? 'general',
        score:          vars.win.score,
        note:           vars.note || undefined,
      }),
    onSuccess: (bm, vars) => {
      setBookmarkedMap(prev => new Map(prev).set(vars.key, bm.id))
      qc.invalidateQueries({ queryKey: ['muhurta-bookmarks'] })
    },
    onSettled: () => setBookmarkingKey(null),
  })

  const deleteBookmark = useMutation({
    mutationFn: (id: string) => muhurtaApi.deleteBookmark(id),
    onSuccess: (_, id) => {
      setBookmarkedMap(prev => {
        const next = new Map(prev)
        for (const [k, v] of next.entries()) if (v === id) next.delete(k)
        return next
      })
      qc.invalidateQueries({ queryKey: ['muhurta-bookmarks'] })
    },
    onSettled: () => setDeletingId(null),
  })

  // ── Derived values ──────────────────────────────────────────────────────────

  const results  = searchMutation.data?.windows ?? []
  const bookmarks = bookmarksData ?? []

  const selectedEventItem = useMemo(
    () => ALL_EVENT_ITEMS.find(e => e.id === selectedEventType),
    [selectedEventType],
  )

  const canSearch = selectedEventType !== null && (
    datePreset !== 0 || (customStart && customEnd && customEnd >= customStart)
  )

  const toggleDayOfWeek = (n: number) => {
    setDaysOfWeek(prev => {
      const next = new Set(prev)
      next.has(n) ? next.delete(n) : next.add(n)
      return next
    })
  }

  const handlePreset = (days: number) => {
    setDatePreset(days)
    if (days !== 0) {
      setCustomStart(dayjs().format('YYYY-MM-DD'))
      setCustomEnd(dayjs().add(days, 'day').format('YYYY-MM-DD'))
    }
  }

  const handleSearch = () => {
    if (!canSearch) return
    setExpandedKey(null)
    searchMutation.mutate()
  }

  const handleBookmark = (win: MuhurtaWindow, note: string) => {
    const key = windowKey(win)
    setBookmarkingKey(key)
    createBookmark.mutate({ key, win, note })
  }

  const handleDeleteBookmark = (id: string) => {
    setDeletingId(id)
    deleteBookmark.mutate(id)
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  if (!gate.isLoading && !gate.allowed) {
    return (
      <div className="max-w-4xl space-y-6">
        <div>
          <h1 className="text-star-white text-xl font-semibold flex items-center gap-2">
            <Search className="w-5 h-5 text-gold" />
            Event Muhurta Finder
          </h1>
          <p className="text-star-dim text-sm mt-1">Find auspicious windows for life events</p>
        </div>
        <div className="card-cosmic">
          <HardGate
            feature="Event Muhurta Finder"
            unlockDescription="Searching auspicious windows for specific life events is a Premium feature. Upgrade to find the best timing for marriages, business launches, travel, and more."
          />
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl space-y-6">

      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-star-white text-xl font-semibold flex items-center gap-2">
            <Search className="w-5 h-5 text-gold" />
            Event Muhurta Finder
            <span className="text-gold text-sm font-display">मुहूर्त</span>
          </h1>
          <p className="text-star-muted text-sm mt-0.5">
            Find the most auspicious windows for your event across a date range
          </p>
        </div>

        {/* Tab switcher */}
        <div className="flex items-center gap-1 bg-cosmic-nebula border border-cosmic-mist rounded-lg p-1">
          {([['find', 'Find Muhurta'], ['bookmarks', `Bookmarks${bookmarks.length > 0 ? ` (${bookmarks.length})` : ''}`]] as const).map(([tab, label]) => (
            <button
              key={tab}
              onClick={() => setPageTab(tab)}
              className={cn(
                'px-3 py-1.5 rounded text-xs font-mono transition-all',
                pageTab === tab
                  ? 'bg-violet/10 text-violet-glow border border-violet/20'
                  : 'text-star-muted hover:text-star-bright',
              )}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Find Muhurta tab ────────────────────────────────────────────────── */}
      {pageTab === 'find' && (
        <div className="space-y-4">

          {/* Wizard progress */}
          <WizardProgress
            selectedEventType={selectedEventType}
            canSearch={!!canSearch}
            hasResults={searchMutation.isSuccess && results.length > 0}
          />

          {/* Step 1: Event type */}
          <div className="card-cosmic p-4 space-y-3">
            <div className="flex items-center gap-2">
              <span className="flex items-center justify-center w-5 h-5 rounded-full bg-violet/20 text-violet-glow text-2xs font-bold">1</span>
              <p className="text-star-bright text-sm font-medium">Choose Event Type</p>
            </div>
            <EventTypeGrid selected={selectedEventType} onSelect={setSelectedEventType} />
          </div>

          {/* Step 2: Date range */}
          <div className="card-cosmic p-4 space-y-3">
            <div className="flex items-center gap-2">
              <span className="flex items-center justify-center w-5 h-5 rounded-full bg-violet/20 text-violet-glow text-2xs font-bold">2</span>
              <p className="text-star-bright text-sm font-medium">Date Range</p>
            </div>
            <DateRangePicker
              preset={datePreset}
              customStart={customStart}
              customEnd={customEnd}
              onPreset={handlePreset}
              onCustomStart={setCustomStart}
              onCustomEnd={setCustomEnd}
            />
          </div>

          {/* Step 3: Filters (collapsible) */}
          <div className="card-cosmic overflow-hidden">
            <button
              onClick={() => setShowFilters(v => !v)}
              className="w-full flex items-center justify-between px-4 py-3 text-left"
            >
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-star-dim" />
                <span className="text-star-soft text-sm font-medium">Filters</span>
                {(minScore > 0 || daysOfWeek.size > 0 || timePreference !== 'all') && (
                  <span className="px-1.5 py-0.5 rounded-full bg-violet/20 text-violet-glow text-2xs font-mono">
                    {[minScore > 0, daysOfWeek.size > 0, timePreference !== 'all'].filter(Boolean).length} active
                  </span>
                )}
              </div>
              {showFilters ? <ChevronUp className="w-4 h-4 text-star-dim" /> : <ChevronDown className="w-4 h-4 text-star-dim" />}
            </button>

            {showFilters && (
              <div className="px-4 pb-4 space-y-4 border-t border-cosmic-mist/50">

                {/* Min score */}
                <div className="space-y-2 pt-3">
                  <div className="flex items-center justify-between">
                    <label className="text-star-dim text-2xs font-mono uppercase tracking-wider">
                      Minimum Score
                    </label>
                    <span className="text-star-bright text-sm font-mono">{minScore}</span>
                  </div>
                  <input
                    type="range"
                    min={0}
                    max={90}
                    step={5}
                    value={minScore}
                    onChange={e => setMinScore(Number(e.target.value))}
                    className="w-full accent-gold"
                  />
                  <div className="flex justify-between text-star-dim text-2xs font-mono">
                    <span>Any</span>
                    <span>90+</span>
                  </div>
                </div>

                {/* Days of week */}
                <div className="space-y-2">
                  <p className="text-star-dim text-2xs font-mono uppercase tracking-wider">Days of Week</p>
                  <div className="flex items-center gap-2">
                    {DAYS_OF_WEEK.map(d => (
                      <button
                        key={d.n}
                        onClick={() => toggleDayOfWeek(d.n)}
                        className={cn(
                          'w-8 h-8 rounded-lg border text-xs font-mono transition-all',
                          daysOfWeek.has(d.n)
                            ? 'bg-violet/10 border-violet/40 text-violet-glow'
                            : 'bg-cosmic-dusk border-cosmic-mist text-star-muted hover:text-star-bright',
                        )}
                        title={d.label}
                      >
                        {d.short}
                      </button>
                    ))}
                    {daysOfWeek.size > 0 && (
                      <button
                        onClick={() => setDaysOfWeek(new Set())}
                        className="text-star-dim text-2xs hover:text-star-soft transition-colors ml-1"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </div>

                {/* Time preference */}
                <div className="space-y-2">
                  <p className="text-star-dim text-2xs font-mono uppercase tracking-wider">Time Preference</p>
                  <div className="flex items-center gap-2 flex-wrap">
                    {(['all', 'morning', 'afternoon', 'evening'] as const).map(t => (
                      <button
                        key={t}
                        onClick={() => setTimePreference(t)}
                        className={cn(
                          'px-3 py-1.5 rounded-lg border text-xs font-mono capitalize transition-all',
                          timePreference === t
                            ? 'bg-gold/10 text-gold border-gold/30'
                            : 'bg-cosmic-dusk text-star-muted border-cosmic-mist hover:text-star-bright',
                        )}
                      >
                        {t === 'all' ? 'Any time' : t}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Location (collapsible) */}
          <div className="card-cosmic overflow-hidden">
            <button
              onClick={() => setShowLocation(v => !v)}
              className="w-full flex items-center justify-between px-4 py-3 text-left"
            >
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-star-dim" />
                <span className="text-star-soft text-sm font-medium">Location</span>
                <span className="text-star-dim text-2xs font-mono">
                  {parseFloat(eventLat).toFixed(2)}°, {parseFloat(eventLon).toFixed(2)}° · {eventTz}
                </span>
              </div>
              {showLocation ? <ChevronUp className="w-4 h-4 text-star-dim" /> : <ChevronDown className="w-4 h-4 text-star-dim" />}
            </button>

            {showLocation && (
              <div className="px-4 pb-4 border-t border-cosmic-mist/50 pt-3 space-y-3">
                <p className="text-star-dim text-2xs">
                  Event location used for local sunrise/sunset and muhurta calculations.
                </p>

                {/* City presets */}
                <div className="flex flex-wrap gap-2">
                  {[
                    { name: 'Mumbai',    lat: '19.0760',  lon: '72.8777',  tz: 'Asia/Kolkata' },
                    { name: 'Delhi',     lat: '28.6139',  lon: '77.2090',  tz: 'Asia/Kolkata' },
                    { name: 'Bengaluru', lat: '12.9716',  lon: '77.5946',  tz: 'Asia/Kolkata' },
                    { name: 'Kolkata',   lat: '22.5726',  lon: '88.3639',  tz: 'Asia/Kolkata' },
                    { name: 'Chennai',   lat: '13.0827',  lon: '80.2707',  tz: 'Asia/Kolkata' },
                  ].map(city => (
                    <button
                      key={city.name}
                      onClick={() => { setEventLat(city.lat); setEventLon(city.lon); setEventTz(city.tz) }}
                      className={cn(
                        'px-3 py-1 rounded-lg text-xs font-mono border transition-all',
                        eventLat === city.lat
                          ? 'bg-gold/10 text-gold border-gold/30'
                          : 'bg-cosmic-dusk text-star-muted border-cosmic-mist hover:text-star-bright',
                      )}
                    >
                      {city.name}
                    </button>
                  ))}
                </div>

                {/* Manual lat/lon */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <label className="text-star-dim text-2xs font-mono">Latitude</label>
                    <input
                      type="number"
                      value={eventLat}
                      onChange={e => setEventLat(e.target.value)}
                      step="0.0001"
                      className="w-full bg-cosmic-dusk border border-cosmic-mist rounded px-2 py-1.5 text-sm text-star-bright font-mono focus:outline-none focus:border-gold/50"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-star-dim text-2xs font-mono">Longitude</label>
                    <input
                      type="number"
                      value={eventLon}
                      onChange={e => setEventLon(e.target.value)}
                      step="0.0001"
                      className="w-full bg-cosmic-dusk border border-cosmic-mist rounded px-2 py-1.5 text-sm text-star-bright font-mono focus:outline-none focus:border-gold/50"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-star-dim text-2xs font-mono">Timezone</label>
                    <input
                      type="text"
                      value={eventTz}
                      onChange={e => setEventTz(e.target.value)}
                      placeholder="Asia/Kolkata"
                      className="w-full bg-cosmic-dusk border border-cosmic-mist rounded px-2 py-1.5 text-sm text-star-bright font-mono focus:outline-none focus:border-gold/50"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Search CTA */}
          <div className="flex items-center gap-3">
            <button
              onClick={handleSearch}
              disabled={!canSearch || searchMutation.isPending}
              className={cn(
                'flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium transition-all',
                canSearch && !searchMutation.isPending
                  ? 'bg-gradient-to-r from-violet to-violet-mid text-white shadow-glow-violet hover:opacity-90'
                  : 'bg-cosmic-dusk text-star-dim border border-cosmic-mist cursor-not-allowed',
              )}
            >
              {searchMutation.isPending
                ? <RefreshCw className="w-4 h-4 animate-spin" />
                : <Search className="w-4 h-4" />
              }
              {searchMutation.isPending ? 'Searching…' : 'Find Auspicious Windows'}
            </button>

            {!selectedEventType && (
              <p className="text-star-dim text-xs">Select an event type to continue</p>
            )}
          </div>

          {/* Error state */}
          {searchMutation.isError && (
            <div className="flex items-center gap-3 p-4 rounded-xl bg-inauspicious/5 border border-inauspicious/20 text-inauspicious">
              <XCircle className="w-4 h-4 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium">Search failed</p>
                <p className="text-2xs mt-0.5 text-inauspicious/70">
                  {searchMutation.error instanceof Error
                    ? searchMutation.error.message
                    : 'Could not fetch muhurta windows. Please try again.'}
                </p>
              </div>
            </div>
          )}

          {/* Results section */}
          {searchMutation.isSuccess && (
            <div className="space-y-4">

              {/* Results header */}
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-star-white text-base font-semibold flex items-center gap-2">
                    {selectedEventItem && <span className="text-xl">{selectedEventItem.emoji}</span>}
                    {selectedEventItem?.label ?? 'Event'} Windows
                    <span className="text-star-dim text-sm font-normal">
                      ({results.length} found)
                    </span>
                  </h2>
                  {searchMutation.data && (
                    <p className="text-star-dim text-2xs font-mono mt-0.5">
                      {dayjs(searchMutation.data.dateRangeStart).format('D MMM')} –{' '}
                      {dayjs(searchMutation.data.dateRangeEnd).format('D MMM YYYY')} ·{' '}
                      {searchMutation.data.totalWindowsScored} days scored
                    </p>
                  )}
                </div>

                {/* Score distribution */}
                {results.length > 0 && (
                  <div className="hidden sm:flex items-center gap-3">
                    {(['excellent', 'good', 'fair'] as const).map(q => {
                      const n = results.filter(w => w.qualityLabel === q).length
                      if (n === 0) return null
                      return (
                        <div key={q} className="text-center">
                          <p className={cn('text-base font-mono font-bold', QUALITY_CONFIG[q].text)}>{n}</p>
                          <p className="text-star-dim text-2xs font-mono capitalize">{q}</p>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>

              {/* Empty state */}
              {results.length === 0 && (
                <div className="flex flex-col items-center py-12 text-center">
                  <Calendar className="w-10 h-10 text-star-dim mb-3" />
                  <p className="text-star-soft text-sm">No auspicious windows found</p>
                  <p className="text-star-dim text-2xs mt-1 max-w-sm">
                    Try relaxing your filters, extending the date range, or removing the minimum score requirement.
                  </p>
                </div>
              )}

              {/* Window list */}
              {results.map((win, i) => {
                const key = windowKey(win)
                return (
                  <ResultRow
                    key={key}
                    win={win}
                    rank={i + 1}
                    isExpanded={expandedKey === key}
                    isBookmarked={bookmarkedMap.has(key)}
                    isBookmarking={bookmarkingKey === key}
                    eventLabel={selectedEventItem?.label ?? 'Event'}
                    onToggle={() => setExpandedKey(expandedKey === key ? null : key)}
                    onBookmark={(note) => handleBookmark(win, note)}
                  />
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Bookmarks tab ──────────────────────────────────────────────────── */}
      {pageTab === 'bookmarks' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-star-white text-base font-semibold">
              Saved Windows
            </h2>
            {bookmarks.length > 0 && (
              <p className="text-star-dim text-2xs font-mono">{bookmarks.length} bookmarked</p>
            )}
          </div>
          <BookmarksPanel
            bookmarks={bookmarks}
            onDelete={handleDeleteBookmark}
            isDeleting={deletingId !== null}
          />
        </div>
      )}
    </div>
  )
}
